// src/context/AuthContext.js
// Updated: 11/10/2025 06:17 PM EST
// --- THIS IS THE FULL, UPDATED FILE ---

import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { auth, db } from '../firebaseConfig';
import { onAuthStateChanged, signOut as firebaseSignOut } from 'firebase/auth';
import { doc, onSnapshot, updateDoc } from 'firebase/firestore';

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);

  const [claims, setClaims] = useState(null);
  const [userDoc, setUserDoc] = useState(null);
  const [userData, setUserData] = useState(null);

  const [isLoading, setIsLoading] = useState(true);
  const [userDocWarning, setUserDocWarning] = useState(null);

  const isAdmin = useMemo(() => {
    const role = userDoc?.role ?? claims?.role;
    const superAdmin = (userDoc?.super_admin ?? claims?.super_admin) === true;
    return superAdmin || role === 'admin';
  }, [userDoc, claims]);

  // Merge claims + userDoc
  useEffect(() => {
    if (!currentUser) {
      setUserData(null);
      return;
    }
    
    // --- 1. CHECK STATUS: If userDoc exists, use its status, defaulting to 'active' ---
    const userStatus = userDoc?.status || 'active';
    
    // If the user is inactive, log them out immediately to prevent access
    if (userStatus === 'inactive') {
      console.warn("AuthContext: User is inactive. Logging out.");
      firebaseSignOut(auth); // Force sign out immediately
      setUserData(null);
      setCurrentUser(null);
      setIsLoading(false);
      return; // Exit effect
    }
    // --- END CHECK ---

    const merged = {
      uid: currentUser.uid,
      email: currentUser.email ?? null,
      role: userDoc?.role ?? claims?.role ?? null,
      orgId: userDoc?.orgId ?? claims?.orgId ?? null,
      super_admin: (userDoc?.super_admin ?? claims?.super_admin) === true,
      emailVerified: currentUser.emailVerified,
      status: userStatus, // Include status
      ...userDoc,
    };
    setUserData(merged);
  }, [currentUser, claims, userDoc]);

  const refreshClaims = async () => {
    if (!auth.currentUser) return null;
    const result = await auth.currentUser.getIdTokenResult(true);
    setClaims(result.claims || {});
    return result.claims || {};
  };

  const logout = () => firebaseSignOut(auth);

  useEffect(() => {
    let unsubscribeUserDoc = () => {};

    const unsubAuth = onAuthStateChanged(auth, async (user) => {
      setIsLoading(true);
      setUserDocWarning(null);
      setCurrentUser(user);

      unsubscribeUserDoc();
      setUserDoc(null);
      setClaims(null);

      if (!user) {
        setIsLoading(false);
        return;
      }

      try {
        const tokenResult = await user.getIdTokenResult(true);
        setClaims(tokenResult.claims || {});
      } catch (e) {
        console.error('AuthContext: failed to get ID token claims:', e);
        setClaims({});
      }

      const userRef = doc(db, 'users', user.uid);
      unsubscribeUserDoc = onSnapshot(
        userRef,
        async (snap) => { 
          if (snap.exists()) {
            const firestoreData = snap.data();
            setUserDoc(firestoreData);

            if (
              user.emailVerified &&
              (firestoreData.emailVerified === false || !firestoreData.hasOwnProperty('emailVerified'))
            ) {
              try {
                await updateDoc(userRef, { emailVerified: true });
                setUserDoc({ ...firestoreData, emailVerified: true });
                console.log('AuthContext: Synced emailVerified status to Firestore.');
              } catch (err) {
                console.error("AuthContext: Failed to sync emailVerified to Firestore.", err);
              }
            }

          } else {
            setUserDoc(null);
          }
          setUserDocWarning(null);
          setIsLoading(false);
        },
        (error) => {
          console.warn('AuthContext Warning: unable to read /users doc. Falling back to claims.', error);
          setUserDoc(null);
          setUserDocWarning('Unable to read your profile document. Using sign-in claims only.');
          setIsLoading(false);
        }
      );
    });

    return () => {
      unsubAuth();
      unsubscribeUserDoc();
    };
  }, []);

  const value = {
    currentUser,
    userData,
    claims,
    userDoc,
    userDocWarning,
    isAdmin,
    isLoading,
    refreshClaims,
    logout,
  };

  return (
    <AuthContext.Provider value={value}>
      {!isLoading && children}
    </AuthContext.Provider>
  );
};